import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Trash2 } from "lucide-react";
import {
  entradaNotaService,
  entradaManualService,
  fornecedorService,
  produtoService,
} from "../../services/apiService";
import {
  Button,
  EmptyState,
  LoadingState,
  SectionCard,
} from "../../components/ui";
import ProductAutocomplete from "../../components/ProductAutocomplete";
import CadastroRapidoModal from "../../components/estoque/CadastroRapidoModal";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import { useNotification } from "../../hooks/useNotification";

const rotulo = {
  IMPORTADA: "Importada",
  EM_CONFERENCIA: "Em conferência",
  CONFERIDA: "Conferida",
  RECEBIMENTO_PARCIAL: "Recebimento parcial",
  COMPLETED: "Recebida",
};
const uuid = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
// Máximo de itens aceito por lote no servidor.
const LIMITE_LOTE = 100;

function Sugestoes({ linha, itens, onEscolher }) {
  const lista = itens?.[linha.id] || [];
  if (linha.produtoId || !lista.length) return null;
  return (
    <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-2 text-sm">
      <div className="font-medium text-blue-900">
        Sugestões — confirmação manual
      </div>
      {lista.map((item) => (
        <button
          key={item.produtoId}
          type="button"
          onClick={() => onEscolher(linha, item.produtoId)}
          className="mt-1 block w-full rounded bg-white p-2 text-left hover:bg-blue-100"
        >
          <span className="font-medium">{item.descricao}</span>
          <span className="ml-2 text-xs text-gray-600">
            {item.confianca}% · {item.motivo}
            {item.unidadeSimbolo ? ` · ${item.unidadeSimbolo}` : ""}
          </span>
        </button>
      ))}
    </div>
  );
}

function Recebimento({
  nota,
  pedidos,
  pedidosCarregando,
  pedidosComErro,
  pedidoId,
  setPedidoId,
  receberAgora,
  setReceberAgora,
  onReceber,
}) {
  const itensPendentes = nota.itens.filter(
    (item) => Number(item.quantidadePendenteRecebimento || 0) > 0,
  );
  const pedidoSelecionado = pedidos.find((pedido) => pedido.id === pedidoId);

  return (
    <div className="mt-6 border-t pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Receber no estoque</h3>
          <p className="mt-1 text-sm text-gray-600">
            Informe somente as quantidades entregues neste recebimento. O
            restante continuará pendente.
          </p>
        </div>
        <span className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
          {itensPendentes.length} item(ns) pendente(s)
        </span>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
        <select
          value={pedidoId}
          onChange={(e) => setPedidoId(e.target.value)}
          className="rounded border bg-white p-2"
        >
          <option value="">Selecione o pedido finalizado</option>
          {pedidos.map((pedido) => (
            <option key={pedido.id} value={pedido.id}>
              Pedido #{pedido.numero}
              {pedido.fornecedorNome ? ` · ${pedido.fornecedorNome}` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!itensPendentes.length}
          onClick={onReceber}
          className="rounded bg-green-600 px-4 py-2 text-white disabled:bg-gray-400"
        >
          {nota.estado === "CONFERIDA"
            ? "Registrar recebimento"
            : "Registrar próximo recebimento"}
        </button>
      </div>

      {pedidosCarregando && (
        <p className="mt-2 text-sm text-gray-600">Carregando pedidos...</p>
      )}
      {!pedidosCarregando && pedidosComErro && (
        <p className="mt-2 text-sm text-red-700">
          Não foi possível carregar os pedidos finalizados.
        </p>
      )}
      {!pedidosCarregando && !pedidosComErro && pedidos.length === 0 && (
        <p className="mt-2 text-sm text-amber-700">
          Nenhum pedido finalizado com quantidade pendente dos produtos desta
          nota.
        </p>
      )}
      {pedidoSelecionado &&
        pedidoSelecionado.fornecedorId !== nota.fornecedorId && (
          <p className="mt-2 text-sm text-amber-700">
            Fornecedor do pedido ({pedidoSelecionado.fornecedorNome || "—"})
            diferente do fornecedor da nota. O recebimento continua permitido.
          </p>
        )}

      <div className="mt-4 overflow-x-auto rounded border">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">
                Produto
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">
                Conferido
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">
                Já recebido
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">
                Pendente
              </th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">
                Receber agora
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {nota.itens.map((item) => {
              const pendente = Number(item.quantidadePendenteRecebimento || 0);
              const unidade = item.unidadeProduto || item.unidadeFiscal || "";

              return (
                <tr key={item.id}>
                  <td className="px-3 py-2">
                    <span className="font-medium">
                      {item.descricaoLimpa || item.descricao}
                    </span>
                    {item.codigoFornecedor && (
                      <small className="block text-gray-500">
                        Código: {item.codigoFornecedor}
                      </small>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {item.quantidadeConferida ?? 0} {unidade}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {item.quantidadeRecebida ?? 0} {unidade}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {pendente} {unidade}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min="0"
                      max={pendente}
                      step="any"
                      disabled={pendente <= 0}
                      value={receberAgora[item.id] ?? ""}
                      onChange={(e) =>
                        setReceberAgora((valores) => ({
                          ...valores,
                          [item.id]: e.target.value,
                        }))
                      }
                      className="w-28 rounded border p-2 text-right disabled:bg-gray-100"
                      aria-label={`Quantidade a receber de ${
                        item.descricaoLimpa || item.descricao
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Detalhe({
  nota,
  fornecedores,
  produtos,
  pedidos,
  pedidosCarregando,
  pedidosComErro,
  contagens,
  observacoes,
  esperadas,
  receberAgora,
  setContagens,
  setObservacoes,
  setEsperadas,
  setReceberAgora,
  pedidoId,
  setPedidoId,
  editavel,
  recebivel,
  onSalvar,
  onFinalizar,
  onReabrir,
  onReceber,
  onDefinirEsperado,
  onFornecedor,
  onCadastrarFornecedor,
  onProduto,
  onCadastrar,
  onCadastrarProdutoCompleto,
  sugestoes,
  onLote,
  produtosCarregando,
  onBuscarProdutos,
}) {
  const [selecionados, setSelecionados] = useState([]);
  const [acoes, setAcoes] = useState({});
  const pendentes = nota.itens.filter((i) => !i.produtoId);
  useEffect(() => {
    // Itens associados fora do lote não podem continuar selecionados.
    const idsPendentes = new Set(
      nota.itens.filter((i) => !i.produtoId).map((i) => i.id),
    );
    setSelecionados((xs) => {
      const restantes = xs.filter((id) => idsPendentes.has(id));
      return restantes.length === xs.length ? xs : restantes;
    });
    setAcoes((xs) => {
      const ids = Object.keys(xs);
      const restantes = ids.filter((id) => idsPendentes.has(id));
      if (restantes.length === ids.length) return xs;
      return Object.fromEntries(restantes.map((id) => [id, xs[id]]));
    });
  }, [nota.itens]);
  const produtoOpcoes = produtos.map((produto) => ({
    value: produto.id,
    label: `${produto.codigo ? `${produto.codigo} · ` : ""}${produto.descricao}`,
  }));
  // Único ponto que altera a seleção por linha: nunca passa do limite do lote.
  const alternar = (id) =>
    setSelecionados((xs) => {
      if (xs.includes(id)) return xs.filter((x) => x !== id);
      if (xs.length >= LIMITE_LOTE) return xs;
      return [...xs, id];
    });
  const limiteAtingido = selecionados.length >= LIMITE_LOTE;
  const atualizarAcao = (id, valor) =>
    setAcoes((xs) => ({ ...xs, [id]: { ...(xs[id] || {}), ...valor } }));
  // Só entram itens sem produto, e no máximo o limite do lote do servidor.
  const idsSelecionaveis = pendentes.slice(0, LIMITE_LOTE).map((i) => i.id);
  // Estado visual: "marcado" só quando TODOS os itens pendentes estão selecionados;
  // com seleção menor (inclusive o lote limitado de 100) fica "parcial".
  const todosSelecionados =
    pendentes.length > 0 && pendentes.every((i) => selecionados.includes(i.id));
  const algunsSelecionados = selecionados.length > 0 && !todosSelecionados;
  // Ação do clique: se o lote que "selecionar todos" marcaria já está selecionado, limpa.
  const loteJaSelecionado =
    idsSelecionaveis.length > 0 &&
    idsSelecionaveis.every((id) => selecionados.includes(id));
  const alternarTodos = () =>
    setSelecionados(loteJaSelecionado ? [] : idsSelecionaveis);
  const processarLote = async () => {
    const itens = selecionados.slice(0, LIMITE_LOTE).map((itemId) => ({
      itemId,
      cadastrarNovo: true,
      ...(acoes[itemId] || {}),
    }));
    try {
      await onLote(itens);
      setSelecionados([]);
      setAcoes({});
    } catch (_) {
      // Mantém a seleção para corrigir e reenviar o lote.
    }
  };
  return (
    <SectionCard>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">
            Nota {nota.numero || nota.chaveAcesso}
          </h2>
          <p className="text-sm text-gray-600">
            Emitente: {nota.emitenteNome || "—"}
          </p>
        </div>
        <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
          {rotulo[nota.estado] || nota.estado}
        </span>
      </div>
      <div className="mt-4 rounded-lg border bg-gray-50 p-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-[16rem] flex-1 text-sm font-medium">
            Fornecedor ativo *
            <select
              disabled={!editavel}
              value={nota.fornecedorId || ""}
              onChange={(e) => e.target.value && onFornecedor(e.target.value)}
              className="mt-1 w-full rounded border bg-white p-2"
            >
              <option value="">Associe o fornecedor emitente</option>
              {fornecedores.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.razaoSocial}
                </option>
              ))}
            </select>
          </label>
          {editavel && (
            <button
              type="button"
              onClick={onCadastrarFornecedor}
              className="rounded border border-blue-300 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
            >
              Novo fornecedor
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          O fornecedor é usado junto do código para manter os vínculos das
          próximas notas.
        </p>
      </div>
      {editavel && pendentes.length > 0 && (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label="Selecionar todos os itens sem produto"
                checked={todosSelecionados}
                ref={(el) => {
                  if (el) el.indeterminate = algunsSelecionados;
                }}
                onChange={alternarTodos}
              />
              <strong>
                Itens sem produto ({selecionados.length} de {pendentes.length}{" "}
                selecionados)
              </strong>
            </label>
            <button
              type="button"
              disabled={!selecionados.length}
              onClick={processarLote}
              className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:bg-gray-400"
            >
              Associar/cadastrar selecionados
            </button>
          </div>
          {pendentes.length > LIMITE_LOTE && (
            <p className="mt-2 text-xs text-amber-800">
              O lote aceita até {LIMITE_LOTE} itens: &quot;selecionar
              todos&quot; marca os primeiros {LIMITE_LOTE} e os demais ficam
              bloqueados até você processar ou desmarcar algum.
            </p>
          )}
          {selecionados.length > 0 && (
            <div className="mt-3 space-y-2">
              {selecionados.map((id) => (
                <div
                  key={id}
                  className="grid gap-2 md:grid-cols-[1fr_1fr_110px]"
                >
                  <div className="flex gap-2">
                    <div className="min-w-0 flex-1">
                      <ProductAutocomplete
                        value={acoes[id]?.produtoId || ""}
                        onChange={(produtoId) =>
                          atualizarAcao(id, {
                            cadastrarNovo: false,
                            produtoId,
                          })
                        }
                        options={produtoOpcoes}
                        onSearch={onBuscarProdutos}
                        searchLoading={produtosCarregando}
                        placeholder={
                          acoes[id]?.cadastrarNovo === false
                            ? "Selecionar produto existente"
                            : "Cadastrar novo produto"
                        }
                      />
                    </div>
                    <Button
                      variant="outline"
                      className="mt-1 min-h-[2.6rem]"
                      disabled={!acoes[id]?.produtoId}
                      title="Remove o produto escolhido; o item será cadastrado como novo"
                      onClick={() =>
                        atualizarAcao(id, {
                          cadastrarNovo: true,
                          produtoId: null,
                        })
                      }
                    >
                      Limpar
                    </Button>
                  </div>
                  <span className="self-center text-sm text-gray-600">
                    {nota.itens.find((i) => i.id === id)?.descricaoLimpa ||
                      nota.itens.find((i) => i.id === id)?.descricao}
                  </span>
                  <input
                    type="number"
                    min="1"
                    placeholder="Qtd. esperada"
                    value={acoes[id]?.quantidadeEsperada || ""}
                    onChange={(e) =>
                      atualizarAcao(id, {
                        quantidadeEsperada: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    className="rounded border p-2"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="mt-5 space-y-3">
        {nota.itens.map((linha) => {
          const esperadoInformado =
              linha.quantidadeSugerida != null ||
              (esperadas[linha.id] != null && esperadas[linha.id] !== ""),
            esperado = esperadoInformado
              ? Number(linha.quantidadeSugerida ?? esperadas[linha.id])
              : null,
            valor = Number(
              contagens[linha.id] ?? linha.quantidadeConferida ?? 0,
            );
          return (
            <div key={linha.id} className="rounded border p-3">
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_110px_220px_90px] md:items-start">
                <div>
                  <div className="flex gap-2">
                    <input
                      type="checkbox"
                      disabled={
                        !editavel ||
                        !!linha.produtoId ||
                        (limiteAtingido && !selecionados.includes(linha.id))
                      }
                      title={
                        limiteAtingido && !selecionados.includes(linha.id)
                          ? `Limite de ${LIMITE_LOTE} itens por lote atingido`
                          : undefined
                      }
                      checked={selecionados.includes(linha.id)}
                      onChange={() => alternar(linha.id)}
                    />
                    <strong>{linha.descricaoLimpa || linha.descricao}</strong>
                  </div>
                  {linha.descricaoLimpa &&
                    linha.descricaoLimpa !== linha.descricao && (
                      <details className="mt-1 text-xs text-gray-500">
                        <summary>Descrição fiscal original</summary>
                        <span>{linha.descricao}</span>
                      </details>
                    )}
                  <small className="block text-gray-500">
                    Código fiscal: {linha.codigoFornecedor || "não informado"} ·
                    Unidade: {linha.unidadeFiscal || "—"}
                    {linha.ean ? ` · EAN: ${linha.ean}` : ""}
                  </small>
                  {!linha.produtoId && (
                    <>
                      <Sugestoes
                        linha={linha}
                        itens={sugestoes}
                        onEscolher={onProduto}
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <ProductAutocomplete
                          value=""
                          onChange={(produtoId) => onProduto(linha, produtoId)}
                          options={produtoOpcoes}
                          onSearch={onBuscarProdutos}
                          searchLoading={produtosCarregando}
                          placeholder="Selecionar produto existente"
                          disabled={!editavel}
                        />
                        <Button
                          className="mt-1 min-h-[2.6rem]"
                          disabled={!editavel}
                          onClick={() => onCadastrar(linha)}
                        >
                          Cadastro automático
                        </Button>
                        <Button
                          variant="outline"
                          className="mt-1 min-h-[2.6rem]"
                          disabled={!editavel}
                          onClick={() => onCadastrarProdutoCompleto(linha)}
                        >
                          Cadastro completo
                        </Button>
                      </div>
                    </>
                  )}
                  {linha.produtoId && (
                    <small className="mt-1 block text-green-700">
                      Produto associado
                    </small>
                  )}
                </div>
                <div className="text-sm">
                  Esperado
                  {linha.quantidadeSugerida == null && linha.produtoId ? (
                    <>
                      <input
                        type="number"
                        min="1"
                        value={esperadas[linha.id] ?? ""}
                        onChange={(e) =>
                          setEsperadas((atual) => ({
                            ...atual,
                            [linha.id]: e.target.value,
                          }))
                        }
                        onBlur={(e) => onDefinirEsperado(linha, e.target.value)}
                        className="mt-1 w-full rounded border p-2"
                        disabled={!editavel}
                        aria-label={`Quantidade esperada de ${linha.descricao}`}
                      />
                      <small className="mt-1 block text-amber-700">
                        Informe na unidade do produto
                        {linha.unidadeProduto
                          ? ` (${linha.unidadeProduto})`
                          : ""}
                        .
                      </small>
                      {linha.quantidadeComercial != null && (
                        <small className="mt-1 block text-gray-500">
                          Quantidade fiscal: {linha.quantidadeComercial}{" "}
                          {linha.unidadeFiscal || ""}
                        </small>
                      )}
                    </>
                  ) : (
                    <>
                      <br />
                      <strong>
                        {esperado}{" "}
                        {linha.unidadeProduto || linha.unidadeFiscal || ""}
                      </strong>
                    </>
                  )}
                </div>
                <div>
                  <label className="text-sm">
                    Contagem física
                    <input
                      disabled={!editavel || !esperadoInformado}
                      type="number"
                      min="0"
                      value={
                        contagens[linha.id] ?? linha.quantidadeConferida ?? 0
                      }
                      onChange={(e) =>
                        setContagens((x) => ({
                          ...x,
                          [linha.id]: e.target.value,
                        }))
                      }
                      onBlur={() => onSalvar(linha)}
                      className="mt-1 w-full rounded border p-2"
                    />
                  </label>
                  {esperadoInformado && valor !== esperado && (
                    <input
                      disabled={!editavel}
                      placeholder="Observação obrigatória"
                      value={observacoes[linha.id] ?? linha.observacao ?? ""}
                      onChange={(e) =>
                        setObservacoes((x) => ({
                          ...x,
                          [linha.id]: e.target.value,
                        }))
                      }
                      onBlur={() => onSalvar(linha)}
                      className="mt-2 w-full rounded border p-2 text-sm"
                    />
                  )}
                </div>
                <div
                  className={
                    !esperadoInformado || valor === esperado
                      ? "text-green-700"
                      : "text-red-700"
                  }
                >
                  {esperadoInformado ? valor - esperado : "—"}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {editavel && (
        <button
          type="button"
          onClick={onFinalizar}
          className="mt-5 rounded bg-green-600 px-4 py-2 text-white"
        >
          Marcar como conferida
        </button>
      )}
      {nota.estado === "CONFERIDA" && (
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onReabrir}
            className="rounded border px-4 py-2"
          >
            Reabrir conferência
          </button>
        </div>
      )}
      {recebivel && (
        <Recebimento
          nota={nota}
          pedidos={pedidos}
          pedidosCarregando={pedidosCarregando}
          pedidosComErro={pedidosComErro}
          pedidoId={pedidoId}
          setPedidoId={setPedidoId}
          receberAgora={receberAgora}
          setReceberAgora={setReceberAgora}
          onReceber={onReceber}
        />
      )}
    </SectionCard>
  );
}

export default function EntradaNotaNova() {
  const [notaId, setNotaId] = useState(null),
    [arquivo, setArquivo] = useState(null),
    [contagens, setContagens] = useState({}),
    [observacoes, setObservacoes] = useState({}),
    [receberAgora, setReceberAgora] = useState({}),
    [pedidoId, setPedidoId] = useState(""),
    [cadastroRapido, setCadastroRapido] = useState(null),
    [buscaProduto, setBuscaProduto] = useState(""),
    [esperadas, setEsperadas] = useState({});
  const fila = useRef(Promise.resolve()),
    versoes = useRef({}),
    associacaoFila = useRef(Promise.resolve());
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useNotification();
  const confirmar = useConfirmDialog();
  const notas = useQuery({
    queryKey: ["notas-fiscais"],
    queryFn: entradaNotaService.listar,
    retry: 0,
  });
  const fornecedores = useQuery({
    queryKey: ["fornecedores-ativos"],
    queryFn: () => fornecedorService.listar({ situacao: "ATIVO" }),
    retry: 0,
  });
  const produtos = useQuery({
    queryKey: ["produtos-ativos", "busca", buscaProduto],
    queryFn: () =>
      produtoService.listar({
        busca: buscaProduto,
        situacao: "ATIVO",
        tamanho: 501,
      }),
    enabled: buscaProduto.trim().length >= 3,
    retry: 0,
  });
  const nota = useQuery({
    queryKey: ["nota-fiscal", notaId],
    queryFn: () => entradaNotaService.detalhe(notaId),
    enabled: !!notaId,
    retry: 0,
  });
  const sugestoes = useQuery({
    queryKey: ["sugestoes-produtos-nota", notaId],
    queryFn: () => entradaNotaService.sugestoesProdutosDaNota(notaId),
    enabled: !!notaId,
    retry: 0,
  });
  const recebivel = ["CONFERIDA", "RECEBIMENTO_PARCIAL"].includes(
    nota.data?.estado,
  );
  const pedidos = useQuery({
    queryKey: ["pedidos-entrada", nota.data?.id, nota.data?.estado],
    queryFn: () => entradaManualService.pedidos("", "", nota.data?.id),
    enabled: recebivel && Boolean(nota.data?.id),
    retry: 0,
  });
  const atualizar = (valor) => {
    versoes.current[valor.id] = valor.versao;
    queryClient.setQueryData(["nota-fiscal", valor.id], valor);
    queryClient.invalidateQueries({ queryKey: ["notas-fiscais"] });
  };
  const enfileirarAssociacao = (operacao) => {
    const proxima = associacaoFila.current.then(operacao, operacao);
    associacaoFila.current = proxima.catch(() => {});
    return proxima;
  };
  const importar = useMutation({
    mutationFn: entradaNotaService.importar,
    onSuccess: (v) => {
      setNotaId(v.id);
      atualizar(v);
      setArquivo(null);
      showSuccess("Nota fiscal importada.");
    },
    onError: (e) => showError(e.message),
  });
  const abrir = (id) => {
    setNotaId(id);
    setPedidoId("");
    setReceberAgora({});
    setCadastroRapido(null);
    setEsperadas({});
  };
  const editavel =
    nota.data &&
    !["CONFERIDA", "COMPLETED", "RECEBIMENTO_PARCIAL"].includes(
      nota.data.estado,
    );
  const definirEsperado = (linha, valor) => {
    const quantidade = Number(valor);
    if (!linha.produtoId) return Promise.resolve();
    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      showError("Informe uma quantidade esperada inteira e maior que zero.");
      return Promise.resolve();
    }

    return enfileirarAssociacao(() =>
      entradaNotaService.associarProduto(
        nota.data.id,
        linha.id,
        linha.produtoId,
        quantidade,
      ),
    )
      .then(atualizar)
      .catch((e) => showError(e.message));
  };
  const salvar = (linha) => {
    const atual = nota.data;
    const valorEsperado = linha.quantidadeSugerida ?? esperadas[linha.id] ?? "";
    if (!atual || valorEsperado === "") {
      if (atual && linha.produtoId) {
        showError("Informe a quantidade esperada na unidade do produto.");
      }
      return Promise.resolve();
    }
    const esperado = Number(valorEsperado),
      quantidade = Number(
        contagens[linha.id] ?? linha.quantidadeConferida ?? 0,
      ),
      observacao = (observacoes[linha.id] ?? linha.observacao ?? "").trim();
    if (quantidade !== esperado && !observacao) {
      showError(
        "Informe uma observação quando a contagem for diferente do esperado.",
      );
      return Promise.resolve();
    }
    const operacao = fila.current
      .catch(() => {})
      .then(async () => {
        await associacaoFila.current;
        return entradaNotaService.conferir(atual.id, linha.id, {
          quantidadeConferida: quantidade,
          observacao,
          expectedVersion: versoes.current[atual.id] ?? atual.versao,
        });
      })
      .then(atualizar);
    fila.current = operacao;
    operacao.catch((e) => showError(e.message));
    return operacao;
  };
  const associar = (linha, produtoId) =>
    enfileirarAssociacao(() =>
      entradaNotaService.associarProduto(nota.data.id, linha.id, produtoId),
    )
      .then(atualizar)
      .catch((e) => showError(e.message));
  const cadastrar = (linha) =>
    enfileirarAssociacao(() =>
      entradaNotaService.cadastrarProduto(nota.data.id, linha.id),
    )
      .then(atualizar)
      .then(() => showSuccess("Produto cadastrado e associado ao item."))
      .catch((e) => showError(e.message));
  const abrirCadastroFornecedor = () =>
    setCadastroRapido({ tipo: "fornecedor" });
  const abrirCadastroProduto = (linha) =>
    setCadastroRapido({ tipo: "produto", linha });
  const concluirCadastroRapido = async (cadastro, tipo) => {
    setCadastroRapido(null);
    if (tipo === "fornecedor") {
      await queryClient.invalidateQueries({
        queryKey: ["fornecedores-ativos"],
      });
      await enfileirarAssociacao(() =>
        entradaNotaService.associarFornecedor(nota.data.id, cadastro.id),
      )
        .then(atualizar)
        .catch((e) => showError(e.message));
      return;
    }
    if (tipo === "produto" && cadastroRapido?.linha) {
      await queryClient.invalidateQueries({
        queryKey: ["produtos-ativos"],
      });
      await associar(cadastroRapido.linha, cadastro.id);
    }
  };
  const lote = (itens) =>
    enfileirarAssociacao(async () => {
      const resposta = await entradaNotaService.associarProdutosEmLote(
        nota.data.id,
        {
          expectedVersion: versoes.current[nota.data.id] ?? nota.data.versao,
          itens,
        },
      );
      atualizar(resposta.nota);
      showSuccess(
        `${resposta.associacoes || 0} associação(ões) e ${resposta.cadastros || 0} cadastro(s) processados.`,
      );
      return resposta;
    }).catch((e) => {
      showError(e.message);
      throw e;
    });
  const finalizar = async () => {
    if (
      !(await confirmar({
        title: "Marcar nota como conferida",
        description: "A conferência ficará bloqueada até ser reaberta.",
        variant: "primary",
      }))
    )
      return;
    try {
      await Promise.all([fila.current, associacaoFila.current]);
      const v = await entradaNotaService.finalizarConferencia(nota.data.id, {
        expectedVersion: versoes.current[nota.data.id] ?? nota.data.versao,
      });
      atualizar(v);
      showSuccess("Conferência finalizada.");
    } catch (e) {
      showError(e.message);
    }
  };
  const reabrir = async () => {
    try {
      const v = await entradaNotaService.reabrirConferencia(nota.data.id, {
        expectedVersion: versoes.current[nota.data.id] ?? nota.data.versao,
      });
      atualizar(v);
      showSuccess("Conferência reaberta.");
    } catch (e) {
      showError(e.message);
    }
  };
  const receber = async () => {
    if (!pedidoId) return showError("Selecione o pedido finalizado.");
    const itens = nota.data.itens
      .map((i) => ({
        itemId: i.id,
        quantidade: Number(receberAgora[i.id] || 0),
      }))
      .filter((i) => i.quantidade > 0);
    if (!itens.length) return showError("Informe quantidades para receber.");
    if (
      !(await confirmar({
        title: "Registrar recebimento",
        description:
          "Somente as quantidades informadas serão adicionadas ao estoque.",
        variant: "primary",
      }))
    ) {
      return;
    }
    try {
      const v = await entradaNotaService.receber(nota.data.id, {
        pedidoId,
        expectedVersion: versoes.current[nota.data.id] ?? nota.data.versao,
        requestId: uuid(),
        itens,
      });
      atualizar(v);
      setReceberAgora({});
      setPedidoId("");
      queryClient.invalidateQueries({ queryKey: ["pedidos-entrada"] });
      showSuccess(
        v.estado === "COMPLETED"
          ? "Recebimento concluído."
          : "Recebimento parcial registrado.",
      );
    } catch (e) {
      showError(e.message);
    }
  };
  const excluir = async (item) => {
    if (
      !(await confirmar({
        title: "Excluir nota importada",
        description: "A nota e sua conferência serão removidas.",
        variant: "danger",
      }))
    )
      return;
    try {
      await entradaNotaService.excluir(item.id);
      if (notaId === item.id) setNotaId(null);
      queryClient.invalidateQueries({ queryKey: ["notas-fiscais"] });
      showSuccess("Nota importada excluída.");
    } catch (e) {
      showError(e.message);
    }
  };
  return (
    <div className="space-y-5">
      <SectionCard>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <h2 className="font-semibold">Importar nota fiscal</h2>
            <p className="text-sm text-gray-500">
              XML da NF-e ou PDF da DANFE.
            </p>
          </div>
          <input
            id="arquivo-nf"
            type="file"
            accept=".pdf,.xml,application/pdf,application/xml,text/xml"
            className="sr-only"
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
          />
          <label
            htmlFor="arquivo-nf"
            className="cursor-pointer rounded border px-3 py-2 text-sm"
          >
            {arquivo?.name || "Escolher arquivo"}
          </label>
          <Button
            variant="primary"
            icon={FileUp}
            disabled={!arquivo || importar.isPending}
            onClick={() => importar.mutate(arquivo)}
          >
            {importar.isPending ? "Importando..." : "Importar nota"}
          </Button>
        </div>
      </SectionCard>
      <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        <SectionCard bodyClassName="p-0">
          <div className="border-b px-4 py-3 font-semibold">
            Notas importadas
          </div>
          {notas.isLoading ? (
            <LoadingState message="Carregando notas..." />
          ) : !(notas.data || []).length ? (
            <EmptyState
              title="Nenhuma nota importada"
              description="Importe uma nota fiscal para começar."
            />
          ) : (
            (notas.data || []).map((item) => (
              <div
                key={item.id}
                className={`flex border-b ${notaId === item.id ? "bg-blue-50" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => abrir(item.id)}
                  className="min-w-0 flex-1 p-3 text-left"
                >
                  <strong className="block truncate">
                    NF {item.numero || "sem número"}
                  </strong>
                  <span className="block truncate text-sm text-gray-500">
                    {item.emitenteNome || item.chaveAcesso}
                  </span>
                  <span className="text-xs">
                    {rotulo[item.estado] || item.estado}
                  </span>
                </button>
                {!["COMPLETED", "RECEBIMENTO_PARCIAL"].includes(
                  item.estado,
                ) && (
                  <button
                    type="button"
                    onClick={() => excluir(item)}
                    className="px-3 text-red-600"
                  >
                    <Trash2 size={17} />
                  </button>
                )}
              </div>
            ))
          )}
        </SectionCard>
        {nota.isLoading ? (
          <LoadingState message="Carregando nota fiscal..." />
        ) : nota.data ? (
          <Detalhe
            nota={nota.data}
            fornecedores={fornecedores.data || []}
            produtos={produtos.data || []}
            pedidos={pedidos.data || []}
            pedidosCarregando={pedidos.isLoading}
            pedidosComErro={pedidos.isError}
            contagens={contagens}
            observacoes={observacoes}
            esperadas={esperadas}
            receberAgora={receberAgora}
            setContagens={setContagens}
            setObservacoes={setObservacoes}
            setEsperadas={setEsperadas}
            setReceberAgora={setReceberAgora}
            pedidoId={pedidoId}
            setPedidoId={setPedidoId}
            editavel={editavel}
            recebivel={recebivel}
            onSalvar={salvar}
            onFinalizar={finalizar}
            onReabrir={reabrir}
            onReceber={receber}
            onDefinirEsperado={definirEsperado}
            onFornecedor={(id) =>
              enfileirarAssociacao(() =>
                entradaNotaService.associarFornecedor(nota.data.id, id),
              )
                .then(atualizar)
                .catch((e) => showError(e.message))
            }
            onCadastrarFornecedor={abrirCadastroFornecedor}
            onProduto={associar}
            onCadastrar={cadastrar}
            onCadastrarProdutoCompleto={abrirCadastroProduto}
            sugestoes={sugestoes.data || {}}
            onLote={lote}
            produtosCarregando={produtos.isFetching}
            onBuscarProdutos={setBuscaProduto}
          />
        ) : (
          <EmptyState
            title="Selecione uma nota"
            description="Escolha uma nota importada para conferir ou receber."
          />
        )}
      </div>
      <CadastroRapidoModal
        open={Boolean(cadastroRapido)}
        tipo={cadastroRapido?.tipo || "produto"}
        produtoInicial={
          cadastroRapido?.linha
            ? {
                descricao:
                  cadastroRapido.linha.descricaoLimpa ||
                  cadastroRapido.linha.descricao,
                unidadeSimbolo: cadastroRapido.linha.unidadeFiscal || "",
                fornecedorId: nota.data?.fornecedorId || "",
                codigo: cadastroRapido.linha.codigoFornecedor || "",
              }
            : {}
        }
        onClose={() => setCadastroRapido(null)}
        onCreated={concluirCadastroRapido}
      />
    </div>
  );
}
